import React, { useState, useEffect } from "react";
import { C, PAYMENT_METHODS, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, ReportsTab, useToast,
  MeetingsTab, OperationalPlanTab, exportToCSV, ImportModal
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import { getDonors, createDonor, getPledges, createPledge, getTransactions, createTransaction } from "./supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  HeartHandshake, Users, Plus, Search, CheckCircle, Clock,
  Star, FileText, Calendar, Target, Receipt,
  Award,
  DollarSign, BarChart3, PieChart as PieIcon,
  Crown, Gift, Repeat, UserPlus, Download, Upload
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Donors Page
   ═════════════════════════════════════════════ */
export default function DonorsPage({ user }) {
  const [tab, setTab] = useState("donors");
  const [triggers, setTriggers] = useState({ donor: 0, donation: 0 });
  const perms = getPerms(user);

  // متبرعون حيّون لمصدر التقرير الشهري التراكمي — لا mock.
  const [donorRows, setDonorRows] = useState([]);
  useEffect(() => {
    let alive = true;
    getDonors()
      .then((rows) => { if (alive) setDonorRows((rows || []).map(mapDonorRow)); })
      .catch(() => { if (alive) setDonorRows([]); });
    return () => { alive = false; };
  }, []);

  // تعهدات حيّة لمصدر التقرير الشهري التراكمي — لا mock.
  const [pledgeRows, setPledgeRows] = useState([]);
  useEffect(() => {
    let alive = true;
    getPledges()
      .then((rows) => { if (alive) setPledgeRows((rows || []).map((r) => mapPledgeRow(r))); })
      .catch(() => { if (alive) setPledgeRows([]); });
    return () => { alive = false; };
  }, []);

  // تبرعات حيّة لمصدر التقرير الشهري — من transactions (module="donors") — لا mock.
  const [donationRows, setDonationRows] = useState([]);
  useEffect(() => {
    let alive = true;
    getTransactions("donors")
      .then((rows) => { if (alive) setDonationRows((rows || []).map(mapDonationRow)); })
      .catch(() => { if (alive) setDonationRows([]); });
    return () => { alive = false; };
  }, []);

  const tabs = [
    { key: "donors", label: "المتبرعين", icon: HeartHandshake },
    { key: "donations", label: "التبرعات", icon: Gift },
    { key: "analytics", label: "التحليلات", icon: BarChart3 },
    { key: "pledges", label: "التعهدات", icon: FileText },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  const money = (v) => (typeof v === "number" ? v.toLocaleString("ar-SA") : (v ?? "—"));
  const reportSources = [
    {
      label: "التبرعات", mode: "monthly", dateField: "date", rows: donationRows,
      columns: [
        { key: "number", label: "الرقم" },
        { key: "date", label: "التاريخ" },
        { key: "donor", label: "المتبرع" },
        { key: "type", label: "النوع" },
        { key: "amount", label: "المبلغ", format: money },
      ],
    },
    { label: "المتبرعون", mode: "cumulative", rows: donorRows },
    { label: "التعهدات", mode: "cumulative", rows: pledgeRows },
  ];

  return (
    <div>
      <ModuleHeader icon={HeartHandshake} title="المتبرعين" subtitle="إدارة المتبرعين والتبرعات والتعهدات">
        <MonthlyReportButton moduleTitle="المتبرعين" sources={reportSources} />
      </ModuleHeader>

      {perms.canEdit && (
        <div style={dq.row}>
          <button style={dq.btn} onClick={() => fireQuick("donors", "donor")}>
            <HeartHandshake size={16} color="#d97706" />
            <span>إضافة متبرع جديد</span>
          </button>
          <button style={dq.btn} onClick={() => fireQuick("donations", "donation")}>
            <Gift size={16} color={C.teal} />
            <span>تسجيل تبرع جديد</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "donors" && <DonorsTab canEdit={perms.canEdit} triggerOpen={triggers.donor} />}
      {tab === "donations" && <DonationsTab canEdit={perms.canEdit} triggerOpen={triggers.donation} />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "pledges" && <PledgesTab canEdit={perms.canEdit} />}
      {tab === "meetings" && <MeetingsTab departmentName="المتبرعين" module="donor" />}
      {tab === "plan" && <OperationalPlanTab departmentKey="donors" />}
      {tab === "reports" && <ReportsTab departmentName="المتبرعين" />}
    </div>
  );
}

const dq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: C.white, color: C.primary, border: `1px solid ${C.border}`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

/* ─────────────────────────────────────────────
   ✦ Donors Tab
   ───────────────────────────────────────────── */
const INITIAL_DONOR = { name: "", typeLabel: "فرد", tierLabel: "برونزي", phone: "", email: "", city: "", isRecurringLabel: "لا", notes: "" };

// snake_case (Supabase) → camelCase (الواجهة)
function mapDonorRow(r) {
  return {
    id: r.id,
    name: r.name || "",
    type: r.type || "individual",
    tier: r.tier || "bronze",
    phone: r.phone || "",
    email: r.email || "",
    city: r.city || "",
    totalDonated: Number(r.total_donated) || 0,
    donationsCount: Number(r.donations_count) || 0,
    lastDonation: r.last_donation || "—",
    isRecurring: !!r.is_recurring,
    notes: r.notes || null,
  };
}

// صف transactions (module="donors") → شكل واجهة التبرعات
// الأعمدة: txn_date/txn_type/description/beneficiary/amount/payment_method/invoice_number
function mapDonationRow(r) {
  return {
    id: r.id,
    number: r.invoice_number || "",
    date: r.txn_date || "",
    donor: r.beneficiary || "",
    type: r.txn_type || "",
    description: r.description || "—",
    amount: Number(r.amount) || 0,
    method: r.payment_method || "",
  };
}

function DonorsTab({ canEdit, triggerOpen = 0 }) {
  const [donors, setDonors] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_DONOR);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const tierLabelToKey = { "بلاتيني": "platinum", "ذهبي": "gold", "فضي": "silver", "برونزي": "bronze" };

  async function loadDonors() {
    setLoading(true);
    try {
      const rows = await getDonors();
      setDonors(rows.map(mapDonorRow));
    } catch (e) {
      showToast(e.message || "تعذّر جلب المتبرعين", "error");
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDonors(); }, []);

  async function handleSave() {
    if (!form.name) {
      showToast("الاسم مطلوب", "error");
      return;
    }
    // ملاحظة: جدول donors ليس فيه عمود module (جدول مخصّص لا مقسّم).
    // total_donated / donations_count / last_donation تبقى DEFAULT عند الإدراج
    // وتُحدّث لاحقًا من التبرعات (انظر scripts/donors-table.sql ملاحظة 3).
    setSaving(true);
    try {
      await createDonor({
        name: form.name,
        type: form.typeLabel === "فرد" ? "individual" : "corporate",
        tier: tierLabelToKey[form.tierLabel] || "bronze",
        phone: form.phone || "",
        email: form.email || "",
        city: form.city || "",
        is_recurring: form.isRecurringLabel === "نعم",
        notes: form.notes || null,
      });
      setForm(INITIAL_DONOR);
      setShowAdd(false);
      showToast("تمت إضافة المتبرع");
      await loadDonors();
    } catch (e) {
      showToast(e.message || "تعذّر إضافة المتبرع", "error");
    } finally {
      setSaving(false);
    }
  }

  const DONOR_CSV_COLUMNS = [
    { key: "name",    header: "الاسم" },
    { key: "type",    header: "النوع", value: d => (d.type === "corporate" ? "جهة" : "فرد") },
    { key: "tier",    header: "الفئة" },
    { key: "phone",   header: "الهاتف" },
    { key: "email",   header: "البريد الإلكتروني" },
    { key: "city",    header: "المدينة" },
    { key: "is_recurring", header: "متبرع متكرر", value: d => (d.is_recurring ? "نعم" : "لا") },
    { key: "total_donated", header: "إجمالي التبرعات" },
    { key: "notes",   header: "ملاحظات" },
  ];

  function handleExport() {
    exportToCSV(filtered, DONOR_CSV_COLUMNS, "المتبرعون");
    showToast(`تم تصدير ${filtered.length} متبرع`);
  }

  // الاستيراد يحفظ صفًا صفًا ويتوقف عند أول خطأ فعلي من القاعدة
  async function handleImport(rows) {
    let ok = 0;
    for (const r of rows) {
      if (!r.name) continue;
      try {
        await createDonor({
          name: r.name,
          type: r.type === "جهة" ? "corporate" : "individual",
          tier: tierLabelToKey[r.tier] || "bronze",
          phone: r.phone || "",
          email: r.email || "",
          city: r.city || "",
          is_recurring: r.is_recurring === "نعم",
          notes: r.notes || null,
        });
        ok++;
      } catch (e) {
        showToast(`توقف الاستيراد عند "${r.name}": ${e?.message || "خطأ"}`, "error");
        break;
      }
    }
    setShowImport(false);
    if (ok) { showToast(`تم استيراد ${ok} متبرع`); await loadDonors(); }
  }

  const filtered = donors.filter(d => {
    const matchSearch = d.name.includes(search) || d.phone.includes(search);
    const matchTier = filterTier === "all" || d.tier === filterTier;
    return matchSearch && matchTier;
  });

  const TIERS = [
    { key: "platinum", label: "بلاتيني", color: "#6366f1" },
    { key: "gold", label: "ذهبي", color: C.accent },
    { key: "silver", label: "فضي", color: "#94a3b8" },
    { key: "bronze", label: "برونزي", color: "#d97706" },
  ];

  const tierIcon = (tier) => {
    if (tier === "platinum") return <Crown size={16} color="#6366f1" />;
    if (tier === "gold") return <Star size={16} color={C.accent} />;
    if (tier === "silver") return <Award size={16} color="#94a3b8" />;
    return <HeartHandshake size={16} color="#d97706" />;
  };

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المتبرعين" value={donors.length} icon={Users} color="#d97706" />
        <KPICard title="بلاتيني" value={donors.filter(d => d.tier === "platinum").length} icon={Crown} color="#6366f1" />
        <KPICard title="ذهبي" value={donors.filter(d => d.tier === "gold").length} icon={Star} color={C.accent} />
        <KPICard title="إجمالي التبرعات" value={`${(donors.reduce((s, d) => s + d.totalDonated, 0) / 1000000).toFixed(1)}M`} icon={DollarSign} color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الهاتف..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterTier} onChange={e => setFilterTier(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الفئات</option>
              {TIERS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <Btn icon={Download} variant="ghost" size="sm" onClick={handleExport}>تصدير</Btn>
            {canEdit && <Btn icon={Upload} variant="ghost" size="sm" onClick={() => setShowImport(true)}>استيراد</Btn>}
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة متبرع</Btn>}
          </div>
        </div>
      </Card>

      {loading ? (
        <Card style={{ padding: 40, textAlign: "center", color: C.textLight }}>جارٍ تحميل المتبرعين…</Card>
      ) : filtered.length === 0 ? (
        <Card style={{ padding: 40, textAlign: "center", color: C.textLight }}>
          {donors.length === 0 ? "لا يوجد متبرعون بعد" : "لا نتائج مطابقة للبحث"}
        </Card>
      ) : (
      <div style={styles.donorsGrid}>
        {filtered.map(d => (
          <Card key={d.id} style={styles.donorCard} onClick={() => setSelected(d)}>
            <div style={styles.donorTop}>
              <div style={{
                ...styles.donorAvatar,
                background: d.tier === "platinum" ? "linear-gradient(135deg, #6366f1, #a78bfa)"
                  : d.tier === "gold" ? `linear-gradient(135deg, ${C.accent}, #b8941a)`
                  : d.tier === "silver" ? "linear-gradient(135deg, #94a3b8, #cbd5e1)"
                  : `linear-gradient(135deg, #d97706, #f59e0b)`,
              }}>
                {d.name.charAt(0)}
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: TIERS.find(t => t.key === d.tier)?.color + "15",
                color: TIERS.find(t => t.key === d.tier)?.color,
              }}>
                {tierIcon(d.tier)}
                {TIERS.find(t => t.key === d.tier)?.label}
              </div>
            </div>
            <h4 style={{ margin: "10px 0 2px", fontSize: 15, fontWeight: 700, color: C.primary }}>{d.name}</h4>
            <p style={{ margin: 0, fontSize: 12, color: C.textLight }}>{d.type === "individual" ? "فرد" : "مؤسسة"}</p>

            <div style={styles.donorAmount}>
              <span style={{ fontSize: 20, fontWeight: 700, color: C.success }}>{d.totalDonated.toLocaleString()}</span>
              <span style={{ fontSize: 11, color: C.textLight }}>ريال إجمالي</span>
            </div>

            <div style={styles.donorMeta}>
              <span><Receipt size={12} /> {d.donationsCount} تبرع</span>
              <span><Calendar size={12} /> آخر تبرع: {d.lastDonation}</span>
            </div>
            {d.isRecurring && (
              <div style={styles.recurringBadge}>
                <Repeat size={12} /> متبرع دوري
              </div>
            )}
          </Card>
        ))}
      </div>
      )}

      {selected && (
        <Modal title={`متبرع — ${selected.name}`} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type === "individual" ? "فرد" : "مؤسسة"}</div>
            <div style={styles.detailRow}><strong>الفئة:</strong> {TIERS.find(t => t.key === selected.tier)?.label}</div>
            <div style={styles.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>
            <div style={styles.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.email}</span></div>
            <div style={styles.detailRow}><strong>المدينة:</strong> {selected.city}</div>
            <div style={styles.detailRow}><strong>إجمالي التبرعات:</strong> <span style={{ color: C.success, fontWeight: 700 }}>{selected.totalDonated.toLocaleString()} ريال</span></div>
            <div style={styles.detailRow}><strong>عدد التبرعات:</strong> {selected.donationsCount}</div>
            <div style={styles.detailRow}><strong>آخر تبرع:</strong> {selected.lastDonation}</div>
            <div style={styles.detailRow}><strong>متبرع دوري:</strong> {selected.isRecurring ? "نعم" : "لا"}</div>
            {selected.notes && <div style={styles.detailRow}><strong>ملاحظات:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}

      {showImport && (
        <ImportModal
          title="استيراد المتبرعين"
          entityLabel="متبرع"
          columns={DONOR_CSV_COLUMNS}
          onImport={handleImport}
          onClose={() => setShowImport(false)}
        />
      )}

      {showAdd && (
        <Modal title="إضافة متبرع جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_DONOR); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم الكامل" required placeholder="اسم المتبرع أو المؤسسة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["فرد","مؤسسة"]} required value={form.typeLabel} onChange={v => setForm({ ...form, typeLabel: v })} />
              <Field label="الفئة" type="select" options={["بلاتيني","ذهبي","فضي","برونزي"]} value={form.tierLabel} onChange={v => setForm({ ...form, tierLabel: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="البريد الإلكتروني" placeholder="donor@email.com" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المدينة" placeholder="بريدة" value={form.city} onChange={v => setForm({ ...form, city: v })} />
              <Field label="متبرع دوري" type="select" options={["لا","نعم"]} value={form.isRecurringLabel} onChange={v => setForm({ ...form, isRecurringLabel: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave} disabled={saving}>{saving ? "جارٍ الحفظ…" : "حفظ"}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_DONOR); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Donations Tab
   ───────────────────────────────────────────── */
const INITIAL_DONATION = { donor: "", type: "", date: "", amount: "", method: "", description: "" };

function DonationsTab({ canEdit, triggerOpen = 0 }) {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [donorNames, setDonorNames] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_DONATION);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  // تبرعات حيّة من transactions (module="donors") — لا mock.
  async function loadDonations() {
    setLoading(true);
    try {
      const rows = await getTransactions("donors");
      setDonations((rows || []).map(mapDonationRow));
    } catch (e) {
      showToast(e.message || "تعذّر جلب التبرعات", "error");
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDonations(); }, []);

  // أسماء المتبرعين الحيّة لقائمة اختيار المتبرع — لا mock.
  useEffect(() => {
    let alive = true;
    getDonors()
      .then((rows) => { if (alive) setDonorNames((rows || []).map((r) => r.name).filter(Boolean)); })
      .catch(() => { if (alive) setDonorNames([]); });
    return () => { alive = false; };
  }, []);

  async function handleSave() {
    if (!form.donor || !form.type || !form.date || !form.amount || !form.method) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    setSaving(true);
    try {
      const num = String(donations.length + 1).padStart(3, "0");
      await createTransaction({
        module: "donors",
        direction: "income",
        txn_date: form.date,
        txn_type: form.type,
        description: form.description || "—",
        beneficiary: form.donor,
        amount: Number(form.amount) || 0,
        payment_method: form.method,
        invoice_number: `ت-${num}`,
      });
      setForm(INITIAL_DONATION);
      setShowAdd(false);
      showToast("تم تسجيل التبرع");
      await loadDonations();
    } catch (e) {
      showToast(e.message || "تعذّر تسجيل التبرع", "error");
    } finally {
      setSaving(false);
    }
  }

  const types = [...new Set(donations.map(d => d.type))];
  const filtered = donations.filter(d => {
    const matchSearch = d.donor.includes(search) || d.description.includes(search);
    const matchType = filterType === "all" || d.type === filterType;
    return matchSearch && matchType;
  });
  const total = filtered.reduce((s, d) => s + d.amount, 0);
  const maxDonation = donations.length ? Math.max(...donations.map(d => d.amount)) : 0;

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي التبرعات" value={`${(total / 1000).toFixed(0)}K`} icon={Gift} color={C.success} />
        <KPICard title="عدد التبرعات" value={filtered.length} icon={Receipt} color="#7c3aed" />
        <KPICard title="أكبر تبرع" value={`${(maxDonation / 1000).toFixed(0)}K`} icon={Crown} color={C.accent} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالمتبرع أو الوصف..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تسجيل تبرع</Btn>}
          </div>
        </div>
      </Card>

      {loading ? (
        <Card style={{ padding: 40, textAlign: "center", color: C.textLight }}>جارٍ تحميل التبرعات…</Card>
      ) : filtered.length === 0 ? (
        <Card style={{ padding: 40, textAlign: "center", color: C.textLight }}>
          {donations.length === 0 ? "لا توجد تبرعات بعد" : "لا نتائج مطابقة للبحث"}
        </Card>
      ) : (
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number", label: "الرقم", width: "80px", render: v => <span style={{ fontWeight: 600, color: C.primary }}>{v}</span> },
            { key: "date", label: "التاريخ", width: "100px" },
            { key: "donor", label: "المتبرع" },
            { key: "type", label: "النوع", width: "100px" },
            { key: "description", label: "الوصف" },
            { key: "amount", label: "المبلغ", width: "110px", render: v => <span style={{ fontWeight: 700, color: C.success }}>{v.toLocaleString()}</span> },
            { key: "method", label: "الدفع", width: "100px" },
          ]}
          data={filtered}
        />
      </Card>
      )}

      <div style={styles.totalBar}>
        <span>الإجمالي:</span>
        <span style={{ fontWeight: 700, color: C.success, fontSize: 18 }}>{total.toLocaleString()} ريال</span>
      </div>

      {showAdd && (
        <Modal title="تسجيل تبرع جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_DONATION); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="المتبرع" type="select" options={donorNames.concat(["متبرع جديد"])} required value={form.donor} onChange={v => setForm({ ...form, donor: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["تبرع نقدي","تبرع عيني","زكاة","صدقة","كفالة","وقف"]} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المبلغ (ريال)" type="number" required placeholder="0" value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
              <Field label="طريقة الدفع" type="select" options={PAYMENT_METHODS} required value={form.method} onChange={v => setForm({ ...form, method: v })} />
            </div>
            <Field label="الوصف" type="textarea" placeholder="وصف التبرع..." value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Gift} variant="success" onClick={handleSave} disabled={saving}>{saving ? "جارٍ التسجيل…" : "تسجيل"}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_DONATION); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Analytics Tab
   ───────────────────────────────────────────── */
function AnalyticsTab() {
  // متبرعون حيّون لقائمة أكبر المتبرعين — لا mock.
  const [donors, setDonors] = useState([]);
  useEffect(() => {
    let alive = true;
    getDonors()
      .then((rows) => { if (alive) setDonors((rows || []).map(mapDonorRow)); })
      .catch(() => { if (alive) setDonors([]); });
    return () => { alive = false; };
  }, []);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.chartsRow}>
        <Card style={{ padding: 20, flex: 1 }}>
          <h4 style={styles.chartTitle}><BarChart3 size={18} color={C.teal} /> التبرعات الشهرية</h4>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={MONTHLY_DONATIONS}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ borderRadius: 8, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
              <Bar dataKey="amount" name="التبرعات" fill={C.success} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ padding: 20, flex: 1 }}>
          <h4 style={styles.chartTitle}><PieIcon size={18} color="#7c3aed" /> التبرعات حسب النوع</h4>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={DONATION_TYPES} cx="50%" cy="50%" innerRadius={45} outerRadius={85} dataKey="value" paddingAngle={3}>
                {DONATION_TYPES.map((e, i) => <Cell key={i} fill={TYPE_COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {DONATION_TYPES.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: TYPE_COLORS[i] }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top donors */}
      <Card style={{ padding: 20 }}>
        <h4 style={styles.chartTitle}><Crown size={18} color={C.accent} /> أكبر المتبرعين</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          {[...donors].sort((a, b) => b.totalDonated - a.totalDonated).slice(0, 6).map((d, i) => (
            <div key={d.id} style={styles.topDonorRow}>
              <div style={{
                ...styles.rankBadge,
                background: i === 0 ? `${C.accent}20` : i === 1 ? "#94a3b820" : i === 2 ? "#d9770620" : C.bg,
                color: i === 0 ? C.accent : i === 1 ? "#64748b" : i === 2 ? "#d97706" : C.textLight,
              }}>
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>{d.name}</span>
                <span style={{ fontSize: 12, color: C.textLight, marginRight: 8 }}>({d.type === "individual" ? "فرد" : "مؤسسة"})</span>
              </div>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.success }}>{d.totalDonated.toLocaleString()} ر</span>
              <span style={{ fontSize: 12, color: C.textLight }}>{d.donationsCount} تبرع</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Pledges Tab — التعهدات
   ───────────────────────────────────────────── */
const INITIAL_PLEDGE = { donorId: "", purpose: "", totalAmount: "", frequency: "شهري", startDate: "", endDate: "", notes: "" };

// snake_case (Supabase) → camelCase (الواجهة). donor_id يُحوَّل إلى اسم عرض عبر donorNameById.
// remaining محسوب في العرض (total_amount - collected) وليس عمودًا.
function mapPledgeRow(r, donorNameById = {}) {
  return {
    id: r.id,
    donorId: r.donor_id || "",
    donor: donorNameById[r.donor_id] || "—",
    purpose: r.purpose || "",
    totalAmount: Number(r.total_amount) || 0,
    collected: Number(r.collected) || 0,
    frequency: r.frequency || "",
    startDate: r.start_date || "",
    endDate: r.end_date || "",
    status: r.status || "active",
    notes: r.notes || null,
  };
}

function PledgesTab({ canEdit }) {
  const [pledges, setPledges] = useState([]);
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PLEDGE);
  const [showToast, ToastView] = useToast();

  async function loadData() {
    setLoading(true);
    try {
      // نجلب المتبرعين (لقائمة الاختيار وتحويل donor_id -> اسم) والتعهدات معًا.
      const [donorRows, pledgeRows] = await Promise.all([getDonors(), getPledges()]);
      const nameById = {};
      donorRows.forEach(d => { nameById[d.id] = d.name; });
      setDonors(donorRows);
      setPledges(pledgeRows.map(r => mapPledgeRow(r, nameById)));
    } catch (e) {
      showToast(e.message || "تعذّر جلب التعهدات", "error");
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  async function handleSave() {
    if (!form.donorId || !form.purpose || !form.totalAmount || !form.startDate) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    // collected يبقى DEFAULT 0 في الجدول ولا يُرسل. status افتراضيًا active.
    setSaving(true);
    try {
      await createPledge({
        donor_id: form.donorId,
        purpose: form.purpose,
        total_amount: Number(form.totalAmount) || 0,
        frequency: form.frequency,
        start_date: form.startDate,
        end_date: form.endDate || null,
        status: "active",
        notes: form.notes || null,
      });
      setForm(INITIAL_PLEDGE);
      setShowAdd(false);
      showToast("تم إضافة التعهد");
      await loadData();
    } catch (e) {
      showToast(e.message || "تعذّر إضافة التعهد", "error");
    } finally {
      setSaving(false);
    }
  }

  const PLEDGE_STATUSES = [
    { key: "active", label: "نشط", color: C.success },
    { key: "partial", label: "جزئي", color: C.warning },
    { key: "fulfilled", label: "مكتمل", color: C.teal },
    { key: "overdue", label: "متأخر", color: C.danger },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="التعهدات" value={pledges.length} icon={FileText} color="#6366f1" />
        <KPICard title="إجمالي المتعهد" value={`${(pledges.reduce((s, p) => s + p.totalAmount, 0) / 1000).toFixed(0)}K`} icon={Target} color={C.accent} />
        <KPICard title="المحصّل" value={`${(pledges.reduce((s, p) => s + p.collected, 0) / 1000).toFixed(0)}K`} icon={CheckCircle} color={C.success} />
        <KPICard title="المتبقي" value={`${(pledges.reduce((s, p) => s + (p.totalAmount - p.collected), 0) / 1000).toFixed(0)}K`} icon={Clock} color={C.warning} />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تعهد جديد</Btn>
        </div>
      )}

      {loading ? (
        <Card style={{ padding: 40, textAlign: "center", color: C.textLight }}>جارٍ تحميل التعهدات…</Card>
      ) : pledges.length === 0 ? (
        <Card style={{ padding: 40, textAlign: "center", color: C.textLight }}>لا توجد تعهدات بعد</Card>
      ) : (
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "donor", label: "المتبرع", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "purpose", label: "الغرض" },
            { key: "totalAmount", label: "المبلغ الكلي", width: "110px", render: v => `${v.toLocaleString()} ر` },
            { key: "collected", label: "المحصّل", width: "110px", render: v => <span style={{ color: C.success, fontWeight: 600 }}>{v.toLocaleString()}</span> },
            { key: "remaining", label: "المتبقي", width: "100px", render: (v, row) => <span style={{ color: C.warning }}>{(row.totalAmount - row.collected).toLocaleString()}</span> },
            { key: "frequency", label: "الدورية", width: "90px" },
            { key: "status", label: "الحالة", width: "90px", render: v => <StatusBadge status={v} statuses={PLEDGE_STATUSES} /> },
          ]}
          data={pledges}
        />
      </Card>
      )}

      {showAdd && (
        <Modal title="إضافة تعهد جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_PLEDGE); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="المتبرع" type="select" options={donors.map(d => ({ value: d.id, label: d.name }))} required value={form.donorId} onChange={v => setForm({ ...form, donorId: v })} />
            <Field label="الغرض" required placeholder="مثال: كفالة داعية سنوية" value={form.purpose} onChange={v => setForm({ ...form, purpose: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المبلغ الإجمالي (ريال)" type="number" required placeholder="0" value={form.totalAmount} onChange={v => setForm({ ...form, totalAmount: v })} />
              <Field label="الدورية" type="select" options={["شهري","ربع سنوي","سنوي","لمرة واحدة"]} required value={form.frequency} onChange={v => setForm({ ...form, frequency: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" required value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات (اختياري)..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={FileText} onClick={handleSave} disabled={saving}>{saving ? "جارٍ الحفظ…" : "حفظ التعهد"}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PLEDGE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const MONTHLY_DONATIONS = [];
const DONATION_TYPES = [];
const TYPE_COLORS = [C.success, "#2563eb", "#7c3aed", "#ea580c", "#d97706", "#0891b2"];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  chartsRow: { display: "flex", gap: 16, marginBottom: 16 },
  chartTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  legendGrid: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 8 },
  legendItem: { display: "flex", alignItems: "center", gap: 4 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },
  totalBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", marginTop: 12, background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 500, color: C.primary },

  /* Donors Grid */
  donorsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 },
  donorCard: { padding: 18, cursor: "pointer" },
  donorTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  donorAvatar: {
    width: 48, height: 48, borderRadius: 12, color: C.white,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20,
  },
  donorAmount: { display: "flex", flexDirection: "column", alignItems: "center", marginTop: 14, padding: "12px 0", background: `${C.success}06`, borderRadius: 10 },
  donorMeta: { display: "flex", gap: 20, marginTop: 10, fontSize: 12, color: C.textLight },
  recurringBadge: {
    display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8,
    padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
    background: `${C.teal}12`, color: C.teal,
  },

  /* Analytics */
  topDonorRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.bg}` },
  rankBadge: { width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 },
};
